import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';

gsap.registerPlugin(ScrollTrigger);

const AnimatedCopy = ({
  children,
  className = "",
  delay = 0,
  duration = 1,
  ease = "power4.out",
  stagger = 0.05,
  animateOnScroll = true,
  tag: Tag = "div"
}) => {
  const elementRef = useRef(null);
  const splitTextRef = useRef(null);

  useEffect(() => {
    if (!elementRef.current || !children) return;

    try {
      // Split text into lines
      splitTextRef.current = new SplitType(elementRef.current, {
        types: 'lines,words',
        lineClass: 'split-line'
      });

      const lines = splitTextRef.current?.lines;

      if (!lines || lines.length === 0) {
        console.warn('AnimatedCopy: No lines found after splitting text');
        return;
      }

      if (animateOnScroll) {
        // Animate on scroll
        gsap.fromTo(
          lines,
          {
            y: 100,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: duration,
            ease: ease,
            stagger: stagger,
            delay: delay,
            scrollTrigger: {
              trigger: elementRef.current,
              start: 'top 80%',
              toggleActions: 'play none none none',
            },
          }
        );
      } else {
        // Animate immediately
        gsap.fromTo(
          lines,
          {
            y: 100,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: duration,
            ease: ease,
            stagger: stagger,
            delay: delay,
          }
        );
      }
    } catch (error) {
      console.error('AnimatedCopy error:', error);
      // Fallback: just show the content without animation
      if (elementRef.current) {
        gsap.set(elementRef.current, { opacity: 1 });
      }
    }

    return () => {
      try {
        if (splitTextRef.current) {
          splitTextRef.current.revert();
        }
      } catch (error) {
        console.error('AnimatedCopy cleanup error:', error);
      }
    };
  }, [children, delay, duration, ease, stagger, animateOnScroll]);

  return (
    <Tag ref={elementRef} className={className}>
      {children}
    </Tag>
  );
};

export default AnimatedCopy;

